import {Button, Dialog, TextAreaField} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useState} from 'react';

import {recordRumAction} from '../../util/datadog/datadogRum';
import {
  type CrashReportError,
  useCrashReportDialogStore,
} from './crashReportDialogStore';

export const CRASH_REPORT_SUBMITTED_ACTION = 'crash_report_submitted';

export const CrashReportDialog: React.FC = () => {
  const error = useCrashReportDialogStore(state => state.error);
  const close = useCrashReportDialogStore(state => state.close);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        close();
      }
    },
    [close]
  );

  return (
    <Dialog.Modal isOpen={error != null} onOpenChange={onOpenChange}>
      {/* Remounting per crash is what resets the form, including when a second
          crash arrives while the dialog is still open. */}
      {error != null && (
        <CrashReportForm key={error.message} error={error} onClose={close} />
      )}
    </Dialog.Modal>
  );
};

type CrashReportFormProps = {
  error: CrashReportError;
  onClose: () => void;
};

const CrashReportForm = ({error, onClose}: CrashReportFormProps) => {
  const [comment, setComment] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const submit = useCallback(() => {
    recordRumAction(CRASH_REPORT_SUBMITTED_ACTION, {
      comment,
      errorMessage: error.message,
    });
    setHasSubmitted(true);
  }, [comment, error]);

  return (
    <>
      <Dialog.Heading>
        {hasSubmitted
          ? 'Thanks for the report'
          : "It looks like we're having issues"}
      </Dialog.Heading>
      <Dialog.Content>
        <Tailwind>
          {hasSubmitted ? (
            <p className="m-0 text-[light-dark(var(--moon-700),_var(--moon-300))]">
              Your report is on its way to the team.
            </p>
          ) : (
            <div className="flex flex-col gap-12">
              <p className="m-0 text-[light-dark(var(--moon-700),_var(--moon-300))]">
                Our team has been notified. If you&apos;d like to help, tell us
                what happened below.
              </p>
              <TextAreaField
                label="What happened?"
                value={comment}
                onChange={setComment}
                autoFocus_I_READ_THE_JSDOC
                fullWidth
                rows={4}
              />
            </div>
          )}
        </Tailwind>
      </Dialog.Content>
      <Dialog.Footer>
        {hasSubmitted ? (
          <Button onPress={onClose}>Close</Button>
        ) : (
          <>
            <Button onPress={submit} isDisabled={comment.trim() === ''}>
              Submit crash report
            </Button>
            <Button variant="secondary" onPress={onClose}>
              Cancel
            </Button>
          </>
        )}
      </Dialog.Footer>
    </>
  );
};
